import Cookies from 'js-cookie';
import axios from 'axios';

/**
 * Decodes a JWT token payload without verification.
 * @param token - JWT token string
 * @returns Decoded payload or null if invalid
 */
function decodeToken(token) {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(atob(base64));
    }
    catch {
        return null;
    }
}
/**
 * Creates a parameterized auth module for a specific app.
 * @param config - App-specific auth configuration
 * @returns Object containing all auth functions
 */
function createAuthModule(config) {
    const { cookieName, defaultSignupRole, clientName } = config;
    /**
     * Gets the stored JWT token if it exists and is not expired.
     * @returns Token string or null
     */
    function getToken() {
        const token = Cookies.get(cookieName);
        if (!token)
            return null;
        const payload = decodeToken(token);
        if (!payload)
            return null;
        if (payload.exp * 1000 < Date.now()) {
            Cookies.remove(cookieName);
            return null;
        }
        return token;
    }
    /**
     * Stores a JWT token in cookies.
     * @param token - JWT token string
     */
    function setToken(token) {
        Cookies.set(cookieName, token, { expires: 7 });
    }
    /**
     * Removes the stored JWT token.
     */
    function removeToken() {
        Cookies.remove(cookieName);
    }
    /**
     * Gets the user info from the stored JWT token.
     * @returns User info or null if not authenticated
     */
    function getUser() {
        const token = getToken();
        if (!token)
            return null;
        const payload = decodeToken(token);
        return payload?.user ?? null;
    }
    /**
     * Checks if the user is currently authenticated.
     * @returns True if a valid token exists
     */
    function isAuthenticated() {
        return getToken() !== null;
    }
    /**
     * Checks if the user's email is confirmed.
     * @returns True if user is confirmed
     */
    function isConfirmed() {
        const user = getUser();
        return user?.confirmed ?? false;
    }
    /**
     * Authenticates with email and password, stores the token.
     * @param email - User email
     * @param password - User password
     * @returns The JWT token
     */
    async function login(email, password) {
        const { data } = await axios.post('/api/v3/auth/login', { email, password });
        setToken(data.token);
        return data.token;
    }
    /**
     * Registers a new user, stores the token.
     * @param userData - Registration data
     * @returns The JWT token
     */
    async function register(userData) {
        const { data } = await axios.post('/api/v3/auth/register', {
            ...userData,
            role: userData.role || defaultSignupRole,
        });
        setToken(data.token);
        return data.token;
    }
    /**
     * Sends a password reset email.
     * @param email - User email
     */
    async function forgotPassword(email) {
        await axios.post('/api/v3/auth/forgot-password', {
            email,
            client: clientName,
        });
    }
    /**
     * Resets password using a reset token.
     * @param resetToken - Password reset token from URL
     * @param password - New password
     */
    async function resetPassword(resetToken, password) {
        await axios.post('/api/v3/auth/reset', {
            reset_password_token: resetToken,
            password,
        });
    }
    /**
     * Requests a confirmation email be resent.
     */
    async function sendConfirmationEmail() {
        const token = getToken();
        await axios.post('/api/v3/auth/send-confirmation-email', null, {
            headers: { Authorization: `Bearer ${token}` },
        });
    }
    /**
     * Refreshes the JWT token.
     * @returns The new JWT token
     */
    async function refreshToken() {
        const token = getToken();
        const { data } = await axios.post('/api/v3/auth/refresh', null, {
            headers: { Authorization: `Bearer ${token}` },
        });
        setToken(data.token);
        return data.token;
    }
    /**
     * Logs the user out by removing the token.
     */
    function logout() {
        removeToken();
        window.location.href = '/auth/login';
    }
    /**
     * Creates an AuthAdapter compatible with AppContextProvider.
     * @returns AuthAdapter object
     */
    function createAuthAdapter() {
        const user = getUser();
        return {
            getToken: () => Promise.resolve(getToken()),
            isAuthenticated: isAuthenticated(),
            isLoading: false,
            user: user
                ? { fullName: `${user.firstName} ${user.lastName}`, email: user.email }
                : null,
        };
    }
    /**
     * Sets up automatic token refresh.
     * Checks every 5 minutes and refreshes if the token expires within 30 minutes.
     */
    function setupTokenRefresh() {
        const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes
        const REFRESH_THRESHOLD = 30 * 60; // 30 minutes in seconds
        setInterval(() => {
            const token = getToken();
            if (!token)
                return;
            try {
                const base64Url = token.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const payload = JSON.parse(atob(base64));
                const timeUntilExpiry = payload.exp - Date.now() / 1000;
                if (timeUntilExpiry > 0 && timeUntilExpiry < REFRESH_THRESHOLD) {
                    refreshToken().catch(() => {
                        if (timeUntilExpiry <= 0) {
                            logout();
                        }
                    });
                }
                else if (timeUntilExpiry <= 0) {
                    logout();
                }
            }
            catch {
                // Invalid token — ignore
            }
        }, REFRESH_INTERVAL);
    }
    return {
        getToken,
        setToken,
        removeToken,
        getUser,
        isAuthenticated,
        isConfirmed,
        login,
        register,
        forgotPassword,
        resetPassword,
        sendConfirmationEmail,
        refreshToken,
        logout,
        createAuthAdapter,
        setupTokenRefresh,
    };
}

export { createAuthModule };
//# sourceMappingURL=auth.js.map
